<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import type { DrillDefinition, MultiSelectDefinition } from 'header/types';
    import { differenceWith, sortBy } from 'lodash-es';
    import SimpleSelect from 'portlet/common/components/SimpleSelect.svelte';
    import { DRILL_BY_NONE_OPTION } from 'portlet/models/settings/settingDefinitions/drillSetting';

    interface Props {
        field: DrillDefinition;
    }

    let { field }: Props = $props();

    type Options = MultiSelectDefinition['options'][number];

    let options = $derived(field.options as Options[]);
    let items = $derived(
        options.map(({ label, key: value }) => ({
            label,
            value,
        })),
    );
    let isMultiSelect = $derived(field.allowMultiple);
    let selectedItems = $state(field.value ? sortBy(field.value, 'order') : []);
    let value = $derived(
        items.filter(({ value: v }) => selectedItems.find(({ key }) => key === v)) || '',
    );
    let selectableOptions = $derived(
        differenceWith(options, selectedItems, (option, setting) => option.key === setting.key),
    );

    let hovering = $state<number | null>(null);
    let select = $state<HTMLSelectElement | null>(null);

    const dispatch = createEventDispatcher();

    const onSingleValueChange = (option: Options) => {
        dispatch('change', {
            field: {
                ...field,
                value:
                    option.key === DRILL_BY_NONE_OPTION.key
                        ? []
                        : [
                              {
                                  key: option.key,
                                  order: 0,
                                  // note: we set `drill` to the same value as `key` which holds true for 99% of cases
                                  // but is not true for autodrill hierarchies (where `drill` should be a particular
                                  // level of the hierarchy based on the current filters applied to the query).
                                  // the correct value of `drill` for autodrill hierarchies is later set via `DrillSettingDefinition.applySettingDependency`
                                  // see:
                                  // packages/portletce/src/portlet/models/settings/settingDefinitions/drillSetting.ts
                                  // packages/portletce/src/header/types/settings.ts#DrillDefinition
                                  drill: option.key,
                              },
                          ],
            },
        });
    };

    const drop = (event: DragEvent | null, target: number) => {
        if (!event || !event.dataTransfer) return;

        event.dataTransfer.dropEffect = 'move';
        const start = Number.parseInt(event.dataTransfer.getData('text/plain'));

        selectedItems = selectedItems.map(({ key, order }) => {
            if (order === start) {
                return { key, order: target, drill: key };
            } else if (order === target) {
                return { key, order: start, drill: key };
            } else {
                return { key, order, drill: key };
            }
        });

        hovering = null;

        dispatch('change', {
            field: {
                ...field,
                value: selectedItems,
            },
        });
    };

    const dragstart = (event: DragEvent | null, start: number) => {
        if (!event || !event.dataTransfer) return;
        handleDragStartForShortcut(event, options[start]);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.dropEffect = 'move';
        event.dataTransfer.setData('text/plain', start.toString());
    };

    const onAddDrill = (event: { currentTarget: HTMLSelectElement }) => {
        dispatch('change', {
            field: {
                ...field,
                value: [
                    ...selectedItems,
                    {
                        key: event.currentTarget.value,
                        order: selectedItems.length,
                        drill: event.currentTarget.value,
                    },
                ],
            },
        });

        if (select) select.selectedIndex = 0;
    };

    const onDeleteDrill = (drillArg: { key: string; order: number; drill: string }) => {
        dispatch('change', {
            field: {
                ...field,
                value: selectedItems
                    .filter(({ key }) => key !== drillArg.key)
                    .map(({ key, order, drill }) => {
                        if (order > drillArg.order) {
                            return { key, drill, order: order - 1 };
                        } else {
                            return { key, order, drill };
                        }
                    }),
            },
        });
    };

    const handleDragStartForShortcut = (e: DragEvent, option: Options) => {
        if (!e.dataTransfer) return;
        e.dataTransfer.dropEffect = 'move';
        e.dataTransfer.setData(
            'setting',
            JSON.stringify({
                label: option.label,
                key: field.key,
                value:
                    option.key === DRILL_BY_NONE_OPTION.key
                        ? []
                        : [
                              {
                                  key: option.key,
                                  drill: option.key,
                                  order: 0,
                              },
                          ],
            }),
        );
    };

    const getDrillLabel = (drill: { key: string; order: number }) => {
        const option = options.find(({ key }) => key === drill.key);
        return option ? option.label : drill.key;
    };

    $effect(() => {
        selectedItems = field.value ? sortBy(field.value, 'order') : [];
    });
</script>

{#if isMultiSelect}
    <select class="select" oninput={onAddDrill} bind:this={select}>
        <option selected disabled hidden>add new drill</option>
        {#each selectableOptions as { key, label } (key)}
            <option value={key}>{label}</option>
        {/each}
    </select>
    <div>
        <div class="list">
            {#each selectedItems as selection, index (selection.key)}
                <div
                    class="list-item"
                    class:list-item__active={hovering === index}
                    draggable={true}
                    ondragstart={event => dragstart(event, index)}
                    ondrop={event => drop(event, index)}
                    ondragend={() => (hovering = null)}
                    ondragover={e => e.preventDefault()}
                    ondragenter={() => (hovering = index)}
                >
                    {getDrillLabel(selection)}
                    <div onclick={() => onDeleteDrill(selection)}>x</div>
                </div>
            {/each}
        </div>
    </div>
{:else}
    <SimpleSelect
        fullWidth
        disabled={field.changeable === false}
        placeholder="None"
        {items}
        {value}
    >
        <li
            class="option"
            class:selected
            let:selected
            slot="item"
            let:item
            draggable="true"
            onclick={() => onSingleValueChange({ ...item, key: item.value })}
            ondragstart={e => handleDragStartForShortcut(e, { ...item, key: item.value })}
        >
            {typeof item === 'object' ? item.label : item}
        </li>
    </SimpleSelect>
{/if}

<style lang="scss" scoped>
    li.option {
        display: block;
        align-items: center;
        padding: 3px 4px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 0.8rem;

        &:hover,
        &:focus {
            outline: none;
            background: #f2f2f2;
        }
        &.selected {
            font-weight: bold;
            background-color: #f2f2f2;
        }
    }
    .list-item {
        display: flex;
        justify-content: space-between;

        padding: 0 4px;
        margin: 2px 0;
        font-size: 12px;
        cursor: pointer;

        background-color: #ededed;

        &:hover {
            background-color: #e4e4e4;
        }

        &__active {
            background-color: #cecece;
        }
    }
</style>
